import { History, LogOut, ReceiptText, User, X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "wouter";

export type AccountBalanceView = {
  cash: string;
  bonus: string;
  locked: string;
  state: "loading" | "ready" | "stale" | "unavailable";
};

type AccountMenuProps = {
  balance: AccountBalanceView;
  displayName: string;
  onLogout: () => Promise<void>;
};

const MOBILE_QUERY = "(max-width: 767px)";
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]):not([tabindex="-1"])';

function balanceStateLabel(state: AccountBalanceView["state"]) {
  switch (state) {
    case "loading":
      return "Loading display balance…";
    case "stale":
      return "Temporarily stale · Display only";
    case "unavailable":
      return "Balance unavailable";
    default:
      return "Display only";
  }
}

export function AccountMenu({
  balance,
  displayName,
  onLogout,
}: AccountMenuProps) {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches,
  );
  const initial = Array.from(displayName.trim())[0]?.toLocaleUpperCase() ?? "?";

  const closeMenu = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) {
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, []);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const handleChange = () => {
      setIsMobile(media.matches);
      closeMenu(false);
    };
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, [closeMenu]);

  useEffect(() => {
    if (!open) return;

    panelRef.current
      ?.querySelector<HTMLElement>("[data-account-menu-initial-focus]")
      ?.focus();

    const previousOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = "hidden";

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closeMenu(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.getAttribute("aria-hidden") !== "true");
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      if (isMobile) document.body.style.overflow = previousOverflow;
    };
  }, [closeMenu, isMobile, open]);

  const handleLogout = async () => {
    setLogoutPending(true);
    try {
      await onLogout();
      closeMenu(false);
    } catch {
      setLogoutPending(false);
    }
  };

  return (
    <div className="v2-account-menu" ref={rootRef}>
      <button
        aria-controls={panelId}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Account menu for ${displayName}`}
        className="v2-account-menu__trigger"
        onClick={() => (open ? closeMenu() : setOpen(true))}
        ref={triggerRef}
        type="button"
      >
        <span className="v2-account-menu__trigger-avatar" aria-hidden>
          {initial}
        </span>
      </button>

      <div
        aria-hidden="true"
        className={`v2-account-menu__backdrop${open ? " is-open" : ""}`}
        onPointerDown={() => closeMenu()}
      />

      <div
        aria-hidden={!open}
        aria-labelledby={`${panelId}-title`}
        aria-modal={isMobile || undefined}
        className={`v2-account-menu__panel${open ? " is-open" : ""}`}
        id={panelId}
        ref={panelRef}
        role="dialog"
      >
        <div className="v2-account-menu__sheet-handle" aria-hidden />
        <button
          aria-label="Close account menu"
          className="v2-account-menu__close"
          onClick={() => closeMenu()}
          tabIndex={isMobile ? 0 : -1}
          type="button"
        >
          <X aria-hidden />
        </button>

        <div className="v2-account-menu__content">
          <Link
            aria-label={`View stable for ${displayName}`}
            className="v2-account-menu__profile"
            data-account-menu-initial-focus
            href="/app/player"
            onClick={() => closeMenu()}
          >
            <span className="v2-account-menu__profile-avatar" aria-hidden>
              {initial}
            </span>
            <span className="v2-account-menu__identity">
              <strong id={`${panelId}-title`}>{displayName}</strong>
              <span>Horse Racing stable</span>
            </span>
          </Link>

          <section
            aria-label="Display-only wallet balance"
            className="v2-account-menu__balance"
          >
            <div className="v2-account-menu__balance-heading">
              <strong>Wallet balance</strong>
              <span>Display only</span>
            </div>
            <dl className="v2-account-menu__balance-grid">
              <div>
                <dt>Cash</dt>
                <dd>{balance.cash}</dd>
              </div>
              <div>
                <dt>Bonus</dt>
                <dd>{balance.bonus}</dd>
              </div>
              <div>
                <dt>Locked</dt>
                <dd>{balance.locked}</dd>
              </div>
            </dl>
            <p
              aria-live="polite"
              className={`v2-account-menu__balance-state is-${balance.state}`}
            >
              {balanceStateLabel(balance.state)}
            </p>
          </section>

          <nav
            aria-label="Account actions"
            className="v2-account-menu__actions"
          >
            <Link href="/app/player" onClick={() => closeMenu()}>
              <User aria-hidden />
              <span>View stable</span>
            </Link>
            <Link href="/app/player/activity" onClick={() => closeMenu()}>
              <ReceiptText aria-hidden />
              <span>Transactions</span>
            </Link>
            <Link href="/app/player/bets" onClick={() => closeMenu()}>
              <History aria-hidden />
              <span>Bet history</span>
            </Link>
            <button
              aria-busy={logoutPending}
              disabled={logoutPending}
              onClick={() => void handleLogout()}
              type="button"
            >
              <LogOut aria-hidden />
              <span>{logoutPending ? "Signing out…" : "Sign out"}</span>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}
